import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  PlayIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  ClockIcon,
  CalendarDaysIcon,
  UserGroupIcon,
  MapPinIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PlayCircleIcon,
  StopCircleIcon,
  EyeIcon,
  DocumentDuplicateIcon,
  CalendarIcon,
  UserIcon,
  AcademicCapIcon,
} from '@heroicons/react/24/outline'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { useAuth } from '@/hooks/useAuth'
import {
  getMealSessions,
  getMealSession,
  getMealTypes,
  createMealSession,
  updateMealSession,
  deleteMealSession,
  startMealSession,
  endMealSession,
  getInstitutionsForMeals,
  type MealSession,
  type MealType,
  type Institution,
} from '@/api/mealsApi'
import { getGrades, getStreams, type Grade, type Stream } from '@/api/academicApi'
import { getUsers, type User } from '@/api/usersApi'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'

const sessionSchema = z.object({
  mealTypeId: z.string().min(1, 'Meal type is required'),
  sessionDate: z.string().min(1, 'Date is required'),
  startTime: z.string().optional(),
  endTime: z.string().optional(),
  targetType: z.enum(['all', 'grades', 'streams']).default('all'),
  targetGrades: z.array(z.string()).optional(),
  targetStreams: z.array(z.string()).optional(),
  location: z.string().optional(),
  supervisorId: z.string().optional(),
  notes: z.string().optional(),
  institutionId: z.string().optional(),
})

type SessionFormData = z.infer<typeof sessionSchema>

const statusColors: Record<string, string> = {
  scheduled: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-green-100 text-green-800',
  completed: 'bg-gray-100 text-gray-800',
  cancelled: 'bg-red-100 text-red-800',
}

const statusLabels: Record<string, string> = {
  scheduled: 'Scheduled',
  in_progress: 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
}

const MealSessionsPage: React.FC = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [sessions, setSessions] = useState<MealSession[]>([])
  const [mealTypes, setMealTypes] = useState<MealType[]>([])
  const [institutions, setInstitutions] = useState<Institution[]>([])
  const [grades, setGrades] = useState<Grade[]>([])
  const [streams, setStreams] = useState<Stream[]>([])
  const [supervisors, setSupervisors] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [mealTypeFilter, setMealTypeFilter] = useState<string>('all')
  const [dateFilter, setDateFilter] = useState<string>('')
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showBulkModal, setShowBulkModal] = useState(false)
  const [selectedSession, setSelectedSession] = useState<MealSession | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)

  // Bulk creation state
  const [bulkDates, setBulkDates] = useState<string[]>([])
  const [bulkStartDate, setBulkStartDate] = useState('')
  const [bulkEndDate, setBulkEndDate] = useState('')
  const [excludeWeekends, setExcludeWeekends] = useState(true)

  const isSuperAdmin = user?.role === 'SUPER_ADMIN'

  const sessionForm = useForm<SessionFormData>({
    resolver: zodResolver(sessionSchema),
    defaultValues: {
      mealTypeId: '',
      sessionDate: new Date().toISOString().split('T')[0],
      targetType: 'all',
      targetGrades: [],
      targetStreams: [],
      location: '',
      supervisorId: '',
      notes: '',
    },
  })

  const watchTargetType = sessionForm.watch('targetType')
  const watchTargetGrades = sessionForm.watch('targetGrades') || []
  const watchTargetStreams = sessionForm.watch('targetStreams') || []

  useEffect(() => {
    fetchSessions()
    fetchMealTypes()
    fetchGrades()
    fetchSupervisors()
    if (isSuperAdmin) {
      fetchInstitutions()
    }
  }, [currentPage, statusFilter, mealTypeFilter, dateFilter])

  useEffect(() => {
    if (watchTargetType === 'streams' && watchTargetGrades.length > 0) {
      fetchStreams(watchTargetGrades[0])
    }
  }, [watchTargetType, watchTargetGrades])

  const fetchSessions = async () => {
    try {
      setLoading(true)
      const response = await getMealSessions({
        page: currentPage,
        limit: 12,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        mealTypeId: mealTypeFilter !== 'all' ? mealTypeFilter : undefined,
        date: dateFilter || undefined,
      })
      setSessions(response.data.sessions)
      setTotalPages(response.data.pagination.totalPages)
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to load sessions')
    } finally {
      setLoading(false)
    }
  }

  const fetchMealTypes = async () => {
    try {
      const response = await getMealTypes({ isActive: true })
      setMealTypes(response.data)
    } catch (error: any) {
      console.error('Failed to load meal types:', error)
    }
  }

  const fetchInstitutions = async () => {
    try {
      const response = await getInstitutionsForMeals()
      setInstitutions(response.data)
    } catch (error: any) {
      console.error('Failed to load institutions:', error)
    }
  }

  const fetchGrades = async () => {
    try {
      const response = await getGrades({ limit: 100, status: 'active' })
      setGrades(response.grades)
    } catch (error: any) {
      console.error('Failed to load grades:', error)
    }
  }

  const fetchStreams = async (gradeId: string) => {
    try {
      const response = await getStreams({ gradeId })
      setStreams(response)
    } catch (error: any) {
      console.error('Failed to load streams:', error)
    }
  }

  const fetchSupervisors = async () => {
    try {
      // Fetch teachers and admins who can supervise meals
      const response = await getUsers({ limit: 100, status: 'active' })
      const eligibleSupervisors = response.users.filter(
        u => u.role === 'Teacher' || u.role === 'School Admin' || u.role === 'SCHOOL_ADMIN' || u.role === 'TEACHER'
      )
      setSupervisors(eligibleSupervisors)
    } catch (error: any) {
      console.error('Failed to load supervisors:', error)
    }
  }

  const handleAdd = async (data: SessionFormData) => {
    try {
      setSubmitting(true)
      await createMealSession({
        mealTypeId: data.mealTypeId,
        sessionDate: data.sessionDate,
        startTime: data.startTime,
        endTime: data.endTime,
        targetType: data.targetType,
        targetGrades: data.targetType === 'grades' ? data.targetGrades : undefined,
        targetStreams: data.targetType === 'streams' ? data.targetStreams : undefined,
        location: data.location,
        supervisorId: data.supervisorId,
        notes: data.notes,
        institutionId: data.institutionId,
      })
      toast.success('Session created successfully')
      setShowAddModal(false)
      sessionForm.reset()
      fetchSessions()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create session')
    } finally {
      setSubmitting(false)
    }
  }

  const handleEdit = async (data: SessionFormData) => {
    if (!selectedSession) return
    try {
      setSubmitting(true)
      await updateMealSession(selectedSession._id, {
        ...data,
        targetGrades: data.targetType === 'grades' ? data.targetGrades : undefined,
        targetStreams: data.targetType === 'streams' ? data.targetStreams : undefined,
      })
      toast.success('Session updated successfully')
      setShowEditModal(false)
      setSelectedSession(null)
      sessionForm.reset()
      fetchSessions()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update session')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async () => {
    if (!selectedSession) return
    try {
      setSubmitting(true)
      await deleteMealSession(selectedSession._id)
      toast.success('Session deleted successfully')
      setShowDeleteModal(false)
      setSelectedSession(null)
      fetchSessions()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete session')
    } finally {
      setSubmitting(false)
    }
  }

  const handleStartSession = async (session: MealSession) => {
    try {
      await startMealSession(session._id)
      toast.success('Session started')
      fetchSessions()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to start session')
    }
  }

  const handleEndSession = async (session: MealSession) => {
    try {
      await endMealSession(session._id)
      toast.success('Session completed')
      fetchSessions()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to end session')
    }
  }

  const handleCloneSession = (session: MealSession) => {
    const mealTypeId = typeof session.mealTypeId === 'object' ? session.mealTypeId._id : session.mealTypeId
    sessionForm.reset({
      mealTypeId,
      sessionDate: new Date().toISOString().split('T')[0],
      startTime: session.startTime,
      endTime: session.endTime,
      targetType: session.targetType,
      targetGrades: session.targetGrades || [],
      targetStreams: session.targetStreams || [],
      location: session.location || '',
      supervisorId: typeof session.supervisorId === 'object' ? session.supervisorId._id : session.supervisorId || '',
      notes: session.notes || '',
    })
    setShowAddModal(true)
  }

  const generateBulkDates = () => {
    if (!bulkStartDate || !bulkEndDate) return

    const start = new Date(bulkStartDate)
    const end = new Date(bulkEndDate)
    const dates: string[] = []

    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const dayOfWeek = d.getDay()
      if (excludeWeekends && (dayOfWeek === 0 || dayOfWeek === 6)) continue
      dates.push(d.toISOString().split('T')[0])
    }

    setBulkDates(dates)
  }

  const handleBulkCreate = async () => {
    const formData = sessionForm.getValues()
    if (!formData.mealTypeId || bulkDates.length === 0) {
      toast.error('Please select a meal type and dates')
      return
    }

    try {
      setSubmitting(true)
      let created = 0
      let failed = 0

      for (const date of bulkDates) {
        try {
          await createMealSession({
            mealTypeId: formData.mealTypeId,
            sessionDate: date,
            startTime: formData.startTime,
            endTime: formData.endTime,
            targetType: formData.targetType,
            targetGrades: formData.targetType === 'grades' ? formData.targetGrades : undefined,
            targetStreams: formData.targetType === 'streams' ? formData.targetStreams : undefined,
            location: formData.location,
            supervisorId: formData.supervisorId,
            notes: formData.notes,
            institutionId: formData.institutionId,
          })
          created++
        } catch (error) {
          failed++
        }
      }

      toast.success(`Created ${created} sessions${failed > 0 ? `, ${failed} failed` : ''}`)
      setShowBulkModal(false)
      setBulkDates([])
      setBulkStartDate('')
      setBulkEndDate('')
      sessionForm.reset()
      fetchSessions()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create sessions')
    } finally {
      setSubmitting(false)
    }
  }

  const openEditModal = (session: MealSession) => {
    setSelectedSession(session)
    const mealTypeId = typeof session.mealTypeId === 'object' ? session.mealTypeId._id : session.mealTypeId
    sessionForm.reset({
      mealTypeId,
      sessionDate: session.sessionDate.split('T')[0],
      startTime: session.startTime,
      endTime: session.endTime,
      targetType: session.targetType,
      targetGrades: session.targetGrades || [],
      targetStreams: session.targetStreams || [],
      location: session.location || '',
      supervisorId: typeof session.supervisorId === 'object' ? session.supervisorId._id : session.supervisorId || '',
      notes: session.notes || '',
    })
    setShowEditModal(true)
  }

  const openDeleteModal = (session: MealSession) => {
    setSelectedSession(session)
    setShowDeleteModal(true)
  }

  const viewAttendance = (session: MealSession) => {
    navigate(`/schoolAdmin/meals/attendance?sessionId=${session._id}`)
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':')
    const h = parseInt(hours)
    const ampm = h >= 12 ? 'PM' : 'AM'
    const displayHour = h % 12 || 12
    return `${displayHour}:${minutes} ${ampm}`
  }

  const getMealTypeName = (session: MealSession) => {
    if (typeof session.mealTypeId === 'object' && session.mealTypeId?.name) {
      return session.mealTypeId.name
    }
    const mt = mealTypes.find(m => m._id === session.mealTypeId)
    return mt?.name || 'Unknown'
  }

  const getSupervisorName = (session: MealSession) => {
    if (typeof session.supervisorId === 'object' && session.supervisorId) {
      return `${session.supervisorId.firstName} ${session.supervisorId.lastName}`
    }
    if (session.supervisorId) {
      const sup = supervisors.find(s => s.id === session.supervisorId)
      if (sup) return `${sup.firstName} ${sup.lastName}`
    }
    return null
  }

  const toggleGrade = (gradeId: string) => {
    const current = watchTargetGrades
    const updated = current.includes(gradeId)
      ? current.filter(id => id !== gradeId)
      : [...current, gradeId]
    sessionForm.setValue('targetGrades', updated)
  }

  const toggleStream = (streamId: string) => {
    const current = watchTargetStreams
    const updated = current.includes(streamId)
      ? current.filter(id => id !== streamId)
      : [...current, streamId]
    sessionForm.setValue('targetStreams', updated)
  }

  const SessionFormContent = ({ isEdit = false }: { isEdit?: boolean }) => (
    <div className="p-6 space-y-4">
      {isSuperAdmin && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Institution
          </label>
          <select
            {...sessionForm.register('institutionId')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">Select Institution</option>
            {institutions.map((inst) => (
              <option key={inst._id} value={inst._id}>
                {inst.name}
              </option>
            ))}
          </select>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Meal Type *
        </label>
        <select
          {...sessionForm.register('mealTypeId')}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="">Select Meal Type</option>
          {mealTypes.map((mt) => (
            <option key={mt._id} value={mt._id}>
              {mt.name} ({formatTime(mt.defaultTime)})
            </option>
          ))}
        </select>
        {sessionForm.formState.errors.mealTypeId && (
          <p className="text-sm text-red-600 mt-1">
            {sessionForm.formState.errors.mealTypeId.message}
          </p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Session Date *
        </label>
        <Input
          type="date"
          {...sessionForm.register('sessionDate')}
          error={sessionForm.formState.errors.sessionDate?.message}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Start Time
          </label>
          <Input
            type="time"
            {...sessionForm.register('startTime')}
          />
          <p className="text-xs text-gray-500 mt-1">Leave blank for default</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            End Time
          </label>
          <Input
            type="time"
            {...sessionForm.register('endTime')}
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Target Students
        </label>
        <select
          {...sessionForm.register('targetType')}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="all">All Students</option>
          <option value="grades">Specific Grades</option>
          <option value="streams">Specific Streams</option>
        </select>
      </div>

      {/* Grade Selection */}
      {watchTargetType === 'grades' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Grades
          </label>
          <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg max-h-40 overflow-y-auto">
            {grades.map((grade) => (
              <button
                key={grade._id}
                type="button"
                onClick={() => toggleGrade(grade._id)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  watchTargetGrades.includes(grade._id)
                    ? 'bg-primary-600 text-white'
                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                }`}
              >
                {grade.name}
              </button>
            ))}
          </div>
          {watchTargetGrades.length > 0 && (
            <p className="text-xs text-gray-500 mt-1">
              {watchTargetGrades.length} grade(s) selected
            </p>
          )}
        </div>
      )}

      {/* Stream Selection */}
      {watchTargetType === 'streams' && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Grade First
            </label>
            <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg">
              {grades.map((grade) => (
                <button
                  key={grade._id}
                  type="button"
                  onClick={() => {
                    sessionForm.setValue('targetGrades', [grade._id])
                    sessionForm.setValue('targetStreams', [])
                    fetchStreams(grade._id)
                  }}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    watchTargetGrades.includes(grade._id)
                      ? 'bg-primary-600 text-white'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {grade.name}
                </button>
              ))}
            </div>
          </div>

          {streams.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Streams
              </label>
              <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg max-h-40 overflow-y-auto">
                {streams.map((stream) => (
                  <button
                    key={stream._id}
                    type="button"
                    onClick={() => toggleStream(stream._id)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      watchTargetStreams.includes(stream._id)
                        ? 'bg-primary-600 text-white'
                        : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {stream.name}
                  </button>
                ))}
              </div>
              {watchTargetStreams.length > 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  {watchTargetStreams.length} stream(s) selected
                </p>
              )}
            </div>
          )}
        </>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Supervisor
        </label>
        <select
          {...sessionForm.register('supervisorId')}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="">Select Supervisor (optional)</option>
          {supervisors.map((sup) => (
            <option key={sup.id} value={sup.id}>
              {sup.firstName} {sup.lastName} ({sup.role})
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Location
        </label>
        <Input
          {...sessionForm.register('location')}
          placeholder="e.g., Main Cafeteria"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Notes
        </label>
        <textarea
          {...sessionForm.register('notes')}
          placeholder="Optional notes..."
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
      </div>
    </div>
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Meal Sessions</h1>
          <p className="text-gray-600 mt-1">
            Schedule and manage meal serving sessions
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => {
              sessionForm.reset({
                sessionDate: new Date().toISOString().split('T')[0],
                targetType: 'all',
                targetGrades: [],
                targetStreams: [],
              })
              setShowBulkModal(true)
            }}
          >
            <CalendarIcon className="h-5 w-5 mr-2" />
            Bulk Create
          </Button>
          <Button onClick={() => {
            sessionForm.reset({
              sessionDate: new Date().toISOString().split('T')[0],
              targetType: 'all',
              targetGrades: [],
              targetStreams: [],
            })
            setShowAddModal(true)
          }}>
            <PlusIcon className="h-5 w-5 mr-2" />
            Schedule Session
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search sessions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          <div className="flex flex-wrap gap-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Status</option>
              <option value="scheduled">Scheduled</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>

            <select
              value={mealTypeFilter}
              onChange={(e) => setMealTypeFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Meal Types</option>
              {mealTypes.map((mt) => (
                <option key={mt._id} value={mt._id}>{mt.name}</option>
              ))}
            </select>

            <Input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="w-auto"
            />

            {(statusFilter !== 'all' || mealTypeFilter !== 'all' || dateFilter) && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setStatusFilter('all')
                  setMealTypeFilter('all')
                  setDateFilter('')
                }}
              >
                Clear Filters
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Sessions Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : sessions.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <CalendarDaysIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No sessions found</h3>
          <p className="text-gray-600 mb-4">
            {statusFilter !== 'all' || mealTypeFilter !== 'all' || dateFilter
              ? 'Try adjusting your filters'
              : 'Get started by scheduling a meal session'}
          </p>
          <Button onClick={() => setShowAddModal(true)}>
            <PlusIcon className="h-5 w-5 mr-2" />
            Schedule Session
          </Button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sessions.map((session) => (
              <motion.div
                key={session._id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
              >
                <div className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {getMealTypeName(session)}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {formatDate(session.sessionDate)}
                      </p>
                    </div>
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[session.status]}`}>
                      {statusLabels[session.status]}
                    </span>
                  </div>

                  <div className="space-y-2 text-sm text-gray-600 mb-4">
                    <div className="flex items-center gap-2">
                      <ClockIcon className="h-4 w-4" />
                      <span>{formatTime(session.startTime)} - {formatTime(session.endTime)}</span>
                    </div>
                    {session.location && (
                      <div className="flex items-center gap-2">
                        <MapPinIcon className="h-4 w-4" />
                        <span>{session.location}</span>
                      </div>
                    )}
                    {getSupervisorName(session) && (
                      <div className="flex items-center gap-2">
                        <UserIcon className="h-4 w-4" />
                        <span>{getSupervisorName(session)}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <UserGroupIcon className="h-4 w-4" />
                      <span>
                        {session.actualCount} / {session.expectedCount} attended
                      </span>
                    </div>
                    {session.targetType !== 'all' && (
                      <div className="flex items-center gap-2">
                        <AcademicCapIcon className="h-4 w-4" />
                        <span className="text-xs">
                          {session.targetType === 'grades'
                            ? `${session.targetGrades?.length || 0} grades`
                            : `${session.targetStreams?.length || 0} streams`}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Progress bar for attendance */}
                  {session.expectedCount > 0 && (
                    <div className="mb-4">
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-green-500 rounded-full transition-all duration-300"
                          style={{ width: `${Math.min((session.actualCount / session.expectedCount) * 100, 100)}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {Math.round((session.actualCount / session.expectedCount) * 100)}% attendance
                      </p>
                    </div>
                  )}

                  {/* Action buttons */}
                  <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
                    {session.status === 'scheduled' && (
                      <Button
                        size="sm"
                        onClick={() => handleStartSession(session)}
                        className="flex-1"
                      >
                        <PlayCircleIcon className="h-4 w-4 mr-1" />
                        Start
                      </Button>
                    )}

                    {session.status === 'in_progress' && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => viewAttendance(session)}
                          className="flex-1"
                        >
                          <UserGroupIcon className="h-4 w-4 mr-1" />
                          Attendance
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleEndSession(session)}
                          className="flex-1"
                        >
                          <StopCircleIcon className="h-4 w-4 mr-1" />
                          End
                        </Button>
                      </>
                    )}

                    {session.status === 'completed' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => viewAttendance(session)}
                        className="flex-1"
                      >
                        <EyeIcon className="h-4 w-4 mr-1" />
                        View Report
                      </Button>
                    )}

                    {/* Clone button for all statuses */}
                    <button
                      onClick={() => handleCloneSession(session)}
                      className="p-2 text-gray-400 hover:text-primary-600 hover:bg-gray-100 rounded-lg"
                      title="Clone Session"
                    >
                      <DocumentDuplicateIcon className="h-4 w-4" />
                    </button>

                    {session.status === 'scheduled' && (
                      <>
                        <button
                          onClick={() => openEditModal(session)}
                          className="p-2 text-gray-400 hover:text-primary-600 hover:bg-gray-100 rounded-lg"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => openDeleteModal(session)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <span className="px-4 py-2 text-sm text-gray-600">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {(showAddModal || showEditModal) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => {
              setShowAddModal(false)
              setShowEditModal(false)
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">
                    {showEditModal ? 'Edit Session' : 'Schedule Session'}
                  </h2>
                  <button
                    onClick={() => {
                      setShowAddModal(false)
                      setShowEditModal(false)
                    }}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <form onSubmit={sessionForm.handleSubmit(showEditModal ? handleEdit : handleAdd)}>
                <SessionFormContent isEdit={showEditModal} />

                <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowAddModal(false)
                      setShowEditModal(false)
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting}>
                    {submitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <CheckCircleIcon className="h-5 w-5 mr-2" />
                        {showEditModal ? 'Update' : 'Schedule'}
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bulk Create Modal */}
      <AnimatePresence>
        {showBulkModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowBulkModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">
                    Bulk Create Sessions
                  </h2>
                  <button
                    onClick={() => setShowBulkModal(false)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Date Range Selection */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-3">Select Date Range</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Start Date
                      </label>
                      <Input
                        type="date"
                        value={bulkStartDate}
                        onChange={(e) => setBulkStartDate(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        End Date
                      </label>
                      <Input
                        type="date"
                        value={bulkEndDate}
                        onChange={(e) => setBulkEndDate(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-4">
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={excludeWeekends}
                        onChange={(e) => setExcludeWeekends(e.target.checked)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      Exclude weekends
                    </label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={generateBulkDates}
                      disabled={!bulkStartDate || !bulkEndDate}
                    >
                      Generate Dates
                    </Button>
                  </div>

                  {bulkDates.length > 0 && (
                    <div className="mt-3">
                      <p className="text-sm text-gray-600 mb-2">
                        {bulkDates.length} sessions will be created:
                      </p>
                      <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                        {bulkDates.map((date) => (
                          <span
                            key={date}
                            className="px-2 py-1 bg-white border border-gray-200 rounded text-xs"
                          >
                            {new Date(date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Session Template Form */}
                <div>
                  <h3 className="font-medium text-gray-900 mb-3">Session Template</h3>
                  <SessionFormContent />
                </div>
              </div>

              <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowBulkModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleBulkCreate}
                  disabled={submitting || bulkDates.length === 0}
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <CheckCircleIcon className="h-5 w-5 mr-2" />
                      Create {bulkDates.length} Sessions
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteModal && selectedSession && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowDeleteModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 bg-red-100 rounded-full">
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Delete Session</h3>
                  <p className="text-sm text-gray-600">This action cannot be undone</p>
                </div>
              </div>

              <p className="text-gray-700 mb-6">
                Are you sure you want to delete this session for{' '}
                <strong>{formatDate(selectedSession.sessionDate)}</strong>?
              </p>

              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  onClick={handleDelete}
                  disabled={submitting}
                >
                  {submitting ? 'Deleting...' : 'Delete'}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default MealSessionsPage
